<template>
  <div>
    <b-card no-body>
      <b-card-header>
        <h4>
          Verified Stock Reports
        </h4>
      </b-card-header>
      <b-card-body>
        <verified-stock-filter
          v-model="filters"
          :rack-group-options="rackGroupOptions"
        />

        <div
          v-if="!(loadingError)"
          class="text-right mb-2"
        >
          <b-button
            variant="outline-primary"
            class="refresh-btn"
            :class="isNewItemAvailable ? 'pr-3' : ''"
            :disabled="loading"
            @click="handleRefresh"
          >
            Refresh
            <span
              v-if="isNewItemAvailable"
              class="badge badge-up badge-pill badge-success badge-minimal badge-glow"
            />
          </b-button>
          <b-button
            variant="outline-primary"
            class="ml-1"
            :disabled="!selectedRGStockReports.length || loading || isDownloading"
            @click="displayPrintRGStockReportModal = true"
          >
            <feather-icon
              icon="PrinterIcon"
              size="16"
            />
            Merge & Print
            <b-spinner
              v-if="isDownloading"
              label="Small Spinner"
              variant="primary"
              small
            />
          </b-button>
        </div>

        <div
          v-if="loading"
          class="text-center"
        >
          <b-spinner
            label="Small Spinner"
            variant="primary"
          />
        </div>

        <b-alert
          variant="danger"
          :show="loadingError ? true : false"
        >
          <div class="alert-body">
            <p>
              {{ loadingError }}
            </p>
          </div>
        </b-alert>

        <b-table
          v-if="!loading && !loadingError"
          :items="verifiedStockReports"
          :fields="tableFields"
          responsive
          striped
          hover
          class="compact-table"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          show-empty
          @sort-changed="sortingChanged"
        >
          <template
            v-for="group in rackGroupOptions"
            v-slot:[`head(${group})`]
          >
            <div
              :key="group"
              style="display:flex"
            >
              <b-form-checkbox
                :disabled="selectableGroupWiseRgReports[group].length === 0"
                @change="groupSelectAllHandler($event, group)"
              />
              <div>{{ `#${group}` }}</div>
            </div>
          </template>

          <template
            v-for="group in rackGroupOptions"
            v-slot:[`cell(${group})`]="{item}"
          >
            <rack-group-stock-reports
              :key="group"
              :rg-stock-reports="item.rack_group_wise_stock_reports.filter(report => report.rack_group_no === group)"
            />
          </template>

          <template #cell(date)="data">
            {{ formatedDate(data.item.date) }}
          </template>

          <template #cell(is_closed)="data">
            <status-icon
              :value="`${data.item.is_closed}`"
              hide-label
            />
          </template>

          <template #cell(invoices)="data">
            <span class="text-wrap">
              {{ getParsedData(data.item.invoices).invoices }}
            </span>
          </template>

          <template #cell(suppliers)="data">
            <span class="text-wrap">
              {{ getParsedData(data.item.invoices).suppliers }}
            </span>
          </template>

          <template #cell(ticket_status)="data">
            <status-icon :value="data.item.ticket_status" />
          </template>

          <template #cell(actions)="data">
            <feather-icon
              icon="EyeIcon"
              size="16"
              @click="handleShowClick(data.item.id)"
            />
          </template>
        </b-table>

        <detailed-pagination
          v-if="!loading && !loadingError"
          :total-items="totalItems"
          :per-page="perPage"
          :current-page="currentPage"
          @page-changed="handlePageChange"
        />
      </b-card-body>
    </b-card>
    <verified-report-details-model
      v-if="selectedId"
      :id="selectedId"
      v-model="dialog"
    />

    <print-r-g-stock-report-modal
      v-if="displayPrintRGStockReportModal"
      :report-ids="selectedRGStockReports"
      :operation-users="operationUsers"
      @modal-closed="displayPrintRGStockReportModal = false"
      @downloaded="clearRGStockReportSelection"
    />

  </div>
</template>

<script>
import {
  BCard, BCardHeader, BTable, BCardBody, BSpinner, BAlert, BFormCheckbox, BButton,
} from 'bootstrap-vue'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import VerifiedReportDetailsModel from '@/components/Stock/VerifiedStockReport/VerifiedReportDetailsModel.vue'
import axios from 'axios'
import dateMixin from '@/mixins/dateMixin'
import StatusIcon from '@/components/UI/StatusIcon.vue'
import VerifiedStockFilter from '@/components/Stock/VerifiedStockReport/VerifiedStockFilter.vue'
import RackGroupStockReports from '@/components/Stock/VerifiedStockReport/RackGroupStockReports.vue'
import PrintRGStockReportModal from '@/components/Stock/VerifiedStockReport/PrintRGStockReportModal.vue'
import getParsedQueryString from '@/utils/headerQueryParser'
import WS, { WS_ROOMS, WS_EVENTS } from '@/utils/ws'
import bus from '@/bus'

export default {
  components: {
    BCard,
    BCardHeader,
    BTable,
    BCardBody,
    DetailedPagination,
    BSpinner,
    BAlert,
    BFormCheckbox,
    BButton,
    VerifiedReportDetailsModel,
    StatusIcon,
    VerifiedStockFilter,
    RackGroupStockReports,
    PrintRGStockReportModal,
  },
  mixins: [dateMixin],
  data() {
    return {
      sortBy: 'date',
      sortDesc: true,
      perPage: 50,
      totalItems: 0,
      currentPage: 1,
      verifiedStockReports: [],
      loading: false,
      loadingError: null,
      isDownloading: false,
      displayPrintRGStockReportModal: false,
      selectedId: null,
      dialog: false,
      rackGroupOptions: [],
      operationUsers: [],
      filters: {
        date: null,
        isClosed: null,
        invoiceNo: null,
        supplier: null,
        productName: null,
        ticketStatus: null,
        ticketNumber: null,
        rackGroups: [],
        rackGroupStatuses: [],
      },
      isNewItemAvailable: false,
    }
  },
  computed: {
    tableFields() {
      const rackGroupColumns = this.rackGroupOptions.map(item => (
        { key: item, label: `#${item}`, thStyle: { width: '6%' } }
      ))

      return [
        { key: 'date', label: 'Date', sortable: true },
        { key: 'invoices', label: 'Invoices' },
        { key: 'suppliers', label: 'Suppliers' },
        ...rackGroupColumns,
        { key: 'ticket_count', label: 'Ticket Count', sortable: false },
        { key: 'ticket_status', label: 'Ticket Status', sortable: false },
        { key: 'is_closed', label: 'Is Closed', sortable: true },
        { key: 'actions', label: 'Actions' },
      ]
    },
    selectableGroupWiseRgReports() {
      const groupWiseReports = {}
      this.rackGroupOptions.forEach(group => {
        groupWiseReports[group] = []
      })

      this.verifiedStockReports.forEach(item => {
        item.rack_group_wise_stock_reports.forEach(report => {
          if ((report.status === 'Created' || report.status === 'Initiated') && groupWiseReports[report.rack_group_no]) {
            groupWiseReports[report.rack_group_no].push(report.id)
          }
        })
      })
      return groupWiseReports
    },
    selectedRGStockReports() {
      return this.$store.getters['stocks/selectedRGStockReports']
    },
  },
  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.selectedId = null
      }
    },
    filters: {
      handler() {
        this.currentPage = 1
        this.fetchVerifiedReports()
      },
      deep: true,
    },
  },
  created() {
    WS.joinRoom(WS_ROOMS.VERIFIED_STOCK_REPORT_STATUS)
    WS.joinRoom(WS_ROOMS.RG_STOCK_REPORT_STATUS)
    bus.$on(WS_EVENTS.VERIFIED_STOCK_REPORT_CREATED, this.onVerifiedStockReportCreated)
    bus.$on(WS_EVENTS.VERIFIED_STOCK_REPORT_UPDATED, this.onVerifiedStockReportUpdated)
    bus.$on(WS_EVENTS.RG_STOCK_REPORT_CREATED, this.onRGStockReportCreated)
    bus.$on(WS_EVENTS.RG_STOCK_REPORT_UPDATED, this.onRGStockReportUpdated)
  },
  destroyed() {
    WS.leaveRoom(WS_ROOMS.VERIFIED_STOCK_REPORT_STATUS)
    WS.leaveRoom(WS_ROOMS.RG_STOCK_REPORT_STATUS)
    bus.$off(WS_EVENTS.VERIFIED_STOCK_REPORT_CREATED, this.onVerifiedStockReportCreated)
    bus.$off(WS_EVENTS.VERIFIED_STOCK_REPORT_UPDATED, this.onVerifiedStockReportUpdated)
    bus.$off(WS_EVENTS.RG_STOCK_REPORT_CREATED, this.onRGStockReportCreated)
    bus.$off(WS_EVENTS.RG_STOCK_REPORT_UPDATED, this.onRGStockReportUpdated)
  },
  mounted() {
    this.fetchVerifiedReports()
    this.fetchOptions()
  },
  beforeDestroy() {
    this.clearRGStockReportSelection()
  },
  methods: {
    getParsedData(data) {
      const invoiceList = data.map(item => item.invoice_no)
      const supplierList = [...new Set(data.map(item => item.supplier__identifier))]
      return {
        invoices: invoiceList.join(', '),
        suppliers: supplierList.join(', '),
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchVerifiedReports()
    },
    async fetchVerifiedReports() {
      try {
        this.loading = true
        this.dialog = false
        this.loadingError = null

        const {
          isClosed, supplier, invoiceNo, date, productName, ticketStatus, ticketNumber, rackGroups, rackGroupStatuses,
        } = this.filters

        const [fromDate, toDate] = date?.split(' to ') || []

        const params = {
          per_page: this.perPage,
          page: this.currentPage,
          sort_by: this.sortBy,
          ...(this.sortDesc ? { sort_desc: this.sortDesc } : {}),
          ...(isClosed !== null ? { is_closed: isClosed } : {}),
          ...(supplier ? { supplier } : {}),
          ...(invoiceNo ? { invoice_no: invoiceNo } : {}),
          ...(fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
          ...(productName ? { product_name: productName } : {}),
          ...(ticketStatus ? { ticket_status: ticketStatus } : {}),
          ...(ticketNumber ? { ticket_number: ticketNumber } : {}),
          ...(rackGroups.length > 0 ? { rack_groups: rackGroups } : {}),
          ...(rackGroupStatuses.length > 0 ? { rack_group_statuses: rackGroupStatuses } : {}),
        }

        const { data } = await axios.get(`/stocks/verified_stock_reports/?${getParsedQueryString(params)}`)
        this.verifiedStockReports = data.results
        this.totalItems = data.count
      } catch (error) {
        this.loadingError = error?.response?.message || 'Failed to fetch verified stocks!'
      } finally {
        this.loading = false
        this.isNewItemAvailable = false
        this.clearRGStockReportSelection()
      }
    },
    handleRefresh() {
      this.fetchVerifiedReports()
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchVerifiedReports()
    },
    handleShowClick(id) {
      this.selectedId = id
      this.dialog = true
    },
    async fetchOptions() {
      this.loading = true
      try {
        const { data } = await axios.get('operations/rack_group_options')
        this.rackGroupOptions = data.group_options
      } catch (error) {
        this.loadingError = error?.response?.data?.detail || 'Failed to fetch RackGroup options!'
        this.loading = false
        return
      }

      try {
        const { data } = await axios.get('operations/operation-users')
        this.operationUsers = data
      } catch (error) {
        this.loadingError = error?.response?.data?.detail || 'Failed to fetch operation users!'
        this.loading = false
        return
      }

      this.loading = false
    },
    groupSelectAllHandler(isSelected, group) {
      const reportIDs = this.selectableGroupWiseRgReports[group]

      if (isSelected) {
        this.$store.dispatch('stocks/selectRGStockReports', { ids: reportIDs })
      } else {
        this.$store.dispatch('stocks/unselectRGStockReports', { ids: reportIDs })
      }
    },
    clearRGStockReportSelection() {
      this.$store.dispatch('stocks/clearRGStockReportSelection')
    },
    onVerifiedStockReportCreated() {
      this.isNewItemAvailable = true
    },
    onVerifiedStockReportUpdated(updatedItem) {
      const itemIndex = this.verifiedStockReports.findIndex(item => item.id === updatedItem.id)
      if (itemIndex === -1) {
        return
      }

      this.verifiedStockReports.splice(itemIndex, 1, updatedItem)
    },
    onRGStockReportCreated() {
      this.isNewItemAvailable = true
    },
    onRGStockReportUpdated(updatedItem) {
      this.verifiedStockReports = this.verifiedStockReports.map(reportItem => {
        const updatedRackGroups = reportItem.rack_group_wise_stock_reports.map(rgStockReport => {
          if (updatedItem.id === rgStockReport.id) {
            return {
              ...rgStockReport,
              ...updatedItem,
            }
          }
          return rgStockReport
        })

        return {
          ...reportItem,
          rack_group_wise_stock_reports: updatedRackGroups,
        }
      })
    },
  },

}
</script>

<style lang="scss">
.refresh-btn {
  position: relative;

  .badge {
    height: 9px;
    width: 9px;
    min-width: 1px;
    min-height: 1px;
    right: 17px;
    top: 14px;
  }

}

</style>
